<template>
  <div class="componentes-tabs">
    <!-- LOADING -->
    <div v-if="loading" class="loading-componentes">
      <b-spinner small></b-spinner>
      <span>Carregando componentes...</span>
    </div>

    <template v-else>
      <!-- FILTROS -->
      <div class="componentes-filtros">
        <div class="periodo-field">
          <label for="periodo-componentes">Período</label>

          <vue-select
            id="periodo-componentes"
            v-model="periodoSelecionado"
            :options="periodosFiltrados"
            :clearable="false"
            :searchable="false"
            :disabled="loading"
            label="label"
            placeholder="Selecione o período"
            @input="alterarPeriodo"
          />
        </div>

        <div class="previsto-realizado-field">
          <FiltroPrevistoRealizado
            v-model="tipoPrevistoRealizado"
            @input="alterarTipoResultado"
          />
        </div>
      </div>

      <!-- LEGENDA -->
      <div class="componentes-legend-wrap">
        <div class="componentes-legend">
          <span
            v-for="classificacao in classificacoes"
            :key="classificacao.key"
            class="legend-item"
          >
            <span
              class="legend-dot"
              :style="{ backgroundColor: classificacao.color }"
            ></span>

            <span class="legend-label">
              {{ classificacao.label }}
            </span>
          </span>
        </div>
      </div>

      <!-- CARDS -->
      <div class="componentes-grid">
        <div
          v-for="item in componentes"
          :key="item.id"
          class="componente-card"
          :style="{ borderColor: corPredominante(item) }"
        >
          <div class="componente-title">
            {{ item.nome }}
          </div>

          <div class="chart-wrap">
            <DonutClassificacao
              :item="item"
              :size="230"
              @select="abrirModalEquipesClassificacao"
            />
          </div>

          <div class="otimas-wrap">
            <div class="otimas-top">
              <span>Equipes Ótimas</span>

              <strong>
                {{ formatarPercentual(item.percentualOtimas) }}%
              </strong>
            </div>

            <div class="otimas-bar">
              <div
                class="otimas-fill"
                :style="{
                  width: limitarPercentual(item.percentualOtimas) + '%',
                }"
              ></div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="!componentes.length" class="empty-state">
        Nenhum componente encontrado para o período selecionado.
      </div>

      <!-- MODAL DAS EQUIPES -->
      <b-modal
        size="xl"
        hide-footer
        centered
        v-model="modalEquipes.visible"
        body-class="p-0"
      >
        <template #modal-title>
          {{ modalEquipes.titulo }}

          <span v-if="periodoSelecionado" class="tag-periodo">
            {{ periodoSelecionado.label }}
          </span>

          <span class="tag-resultado">
            {{ labelTipoResultado }}
          </span>
        </template>

        <div class="p-3">
          <CvatDataTable
            v-if="modalEquipes.indicador === 56"
            :items="modalEquipes.items"
            :loading="modalEquipes.loading"
          />

          <EquipesDataTable
            v-else
            :items="modalEquipes.items"
            :loading="modalEquipes.loading"
          />
        </div>
      </b-modal>
    </template>
  </div>
</template>

<script>
import DonutClassificacao from './DonutClassificacao.vue'
import EquipesDataTable from './EquipesDataTable.vue'
import FiltroPrevistoRealizado from '@/pages/IndicadoresAps/ResultadoMunicipal/components/FiltroPrevistoRealizado.vue'
import CvatDataTable from './CvatDataTable.vue'

export default {
  name: 'Componentes',
  components: {
    DonutClassificacao,
    EquipesDataTable,
    FiltroPrevistoRealizado,
    CvatDataTable,
  },
  data() {
    return {
      loading: false,
      periodoSelecionado: null,
      periodos: [],
      tipoPrevistoRealizado: 'previsto',
      dadosComponentesRaw: [],
      dadosComponentesPeriodo: [],
      componentesDisponiveis: [
        {
          id: 56,
          nome: 'Vínculo e Acompanhamento Territorial (CVAT)',
        },
        {
          id: 57,
          nome: 'Saúde da Família (eSF / eAP)',
        },
        {
          id: 58,
          nome: 'Saúde Bucal (eSB)',
        },
        {
          id: 59,
          nome: 'Equipe Multiprofissional (eMulti)',
        },
      ],
      classificacoes: [
        {
          key: 'otima',
          label: 'Ótimo',
          color: '#1212b6',
        },
        {
          key: 'boa',
          label: 'Bom',
          color: '#38ba38',
        },
        {
          key: 'suficiente',
          label: 'Suficiente',
          color: '#d5ba0a',
        },
        {
          key: 'regular',
          label: 'Regular',
          color: '#c46500',
        },
      ],
      modalEquipes: {
        visible: false,
        loading: false,
        titulo: '',
        items: [],
        indicador: null,
      },
    }
  },
  computed: {
    /**
     * Retorna os períodos permitidos conforme o tipo de resultado selecionado.
     *
     * Para resultados realizados, exibe apenas os meses de fechamento
     * quadrimestral: abril, agosto e dezembro.
     *
     * @return {Array}
     */
    periodosFiltrados() {
      if (this.tipoPrevistoRealizado !== 'realizado') {
        return this.periodos
      }

      return this.periodos.filter((periodo) =>
        [4, 8, 12].includes(Number(periodo.mes))
      )
    },

    /**
     * Retorna o texto referente ao tipo de resultado selecionado.
     *
     * @return {String}
     */
    labelTipoResultado() {
      return this.tipoPrevistoRealizado === 'realizado'
        ? 'Realizado'
        : 'Previsto'
    },

    /**
     * Monta os quatro componentes com os dados do período e do tipo de
     * resultado selecionados.
     *
     * @return {Array}
     */
    componentes() {
      return this.componentesDisponiveis.map((componente) => {
        const row = this.dadosComponentesPeriodo.find(
          (item) => Number(item.indicador) === Number(componente.id)
        )
        return this.montarComponente(componente, row)
      })
    },

    /**
     * Monta o resumo das equipes do período selecionado.
     *
     * Para a quantidade de equipes avaliadas, CVAT e eSF/eAP são agrupados
     * pelo INE, pois podem representar a mesma equipe.
     *
     * Para o percentual de avaliações ótimas, cada resultado de cada indicador
     * é contabilizado separadamente.
     *
     * @return {Object}
     */
    resumoEvolucao() {
      const equipesAgrupadas = new Map()

      let totalAvaliacoes = 0
      let totalAvaliacoesOtimas = 0

      this.componentes.forEach((componente) => {
        const idComponente = Number(componente.id)
        const equipesPorClassificacao = componente.equipes || {}

        totalAvaliacoes += Number(componente.total || 0)
        totalAvaliacoesOtimas += Number(componente.otima || 0)

        Object.entries(equipesPorClassificacao).forEach(
          ([classificacao, equipes]) => {
            if (!Array.isArray(equipes)) {
              return
            }

            equipes.forEach((equipe, indexEquipe) => {
              const ine = String(equipe.ine || equipe.nu_ine || '').trim()
              const cnes = String(equipe.nu_cnes || equipe.cnes || '').trim()

              const identificadorEquipe =
                ine ||
                cnes ||
                `SEM_IDENTIFICADOR-${idComponente}-${classificacao}-${indexEquipe}`

              const chaveEquipe = this.getChaveEquipeResumo(
                idComponente,
                identificadorEquipe
              )

              if (!equipesAgrupadas.has(chaveEquipe)) {
                equipesAgrupadas.set(chaveEquipe, {
                  chave: chaveEquipe,
                  ine,
                  cnes,
                  noEquipe: equipe.no_equipe || '',
                })
              }
            })
          }
        )
      })

      return {
        totalEquipes: equipesAgrupadas.size,
        totalAvaliacoes,
        totalAvaliacoesOtimas,
      }
    },
  },
  mounted() {
    this.carregarComponentes()
  },
  methods: {
    /**
     * Retorna a chave usada para agrupar uma equipe no resumo.
     *
     * Apenas equipes com INE podem ser agrupadas entre os
     * componentes 56 (CVAT) e 57 (Qualidade eSF/eAP).
     * Equipes sem INE são sempre consideradas independentes.
     *
     * @param {Number} indicador
     * @param {String} identificadorEquipe
     * @return {String}
     */
    getChaveEquipeResumo(indicador, identificadorEquipe) {
      const idIndicador = Number(indicador)

      if (
        [56, 57].includes(idIndicador) &&
        !identificadorEquipe.startsWith('SEM_IDENTIFICADOR-')
      ) {
        return `ESF_EAP-${identificadorEquipe}`
      }

      return `${idIndicador}-${identificadorEquipe}`
    },

    /**
     * Informa ao componente pai o período, o tipo de resultado,
     * os componentes filtrados e o resumo das equipes.
     *
     * @return {void}
     */
    emitirPeriodoSelecionado() {
      this.$emit('dados-alterados', {
        periodo: this.periodoSelecionado,
        tipoResultado: this.tipoPrevistoRealizado,
        componentes: this.componentes,

        totalEquipes: this.resumoEvolucao.totalEquipes,
        totalAvaliacoes: this.resumoEvolucao.totalAvaliacoes,
        totalAvaliacoesOtimas: this.resumoEvolucao.totalAvaliacoesOtimas,
      })
    },

    /**
     * Carrega todos os componentes previstos e realizados.
     *
     * A API é consultada apenas uma vez. As alterações posteriores de período
     * e tipo de resultado são realizadas no front.
     *
     * @return {Promise<void>}
     */
    async carregarComponentes() {
      try {
        this.loading = true
        this.resetarDados()

        const url =
          `/app/indicadores-aps/resultado-indicadores/` +
          `${this.$store.state.cod_ibge}/componentes`

        const response = await this.$api.get(url)

        this.dadosComponentesRaw = Array.isArray(response?.data)
          ? response.data
          : []

        this.prepararPeriodos()
        this.filtrarComponentesPorPeriodo()
        this.emitirPeriodoSelecionado()
      } catch (error) {
        console.error('Erro ao carregar os componentes:', error)
        this.resetarDados()
      } finally {
        this.loading = false
      }
    },

    /**
     * Limpa os dados utilizados pelo componente.
     *
     * @return {void}
     */
    resetarDados() {
      this.periodoSelecionado = null
      this.periodos = []
      this.dadosComponentesRaw = []
      this.dadosComponentesPeriodo = []

      this.modalEquipes.visible = false
      this.modalEquipes.loading = false
      this.modalEquipes.titulo = ''
      this.modalEquipes.items = []
      this.modalEquipes.indicador = null
    },

    /**
     * Monta as opções de período usando os dados retornados pela API.
     *
     * @return {void}
     */
    prepararPeriodos() {
      const periodoAnterior = this.periodoSelecionado?.value
      const periodosUnicos = new Map()

      this.dadosComponentesRaw.forEach((item) => {
        const mesAno = String(item.mes_ano || '').trim()
        const mesAnoQuad = String(item.mes_ano_quad || mesAno).trim()

        if (!mesAno) {
          return
        }

        const partes = mesAno.split('/')

        if (partes.length !== 2) {
          return
        }

        const mes = Number(partes[0])
        const ano = Number(partes[1])

        if (
          !Number.isInteger(mes) ||
          !Number.isInteger(ano) ||
          mes < 1 ||
          mes > 12
        ) {
          return
        }

        if (!periodosUnicos.has(mesAno)) {
          periodosUnicos.set(mesAno, {
            label: mesAnoQuad,
            value: mesAno,
            mes,
            ano,
          })
        }
      })

      this.periodos = Array.from(periodosUnicos.values()).sort((a, b) => {
        if (a.ano !== b.ano) {
          return b.ano - a.ano
        }

        return b.mes - a.mes
      })

      const periodoExistente = this.periodos.find(
        (periodo) => periodo.value === periodoAnterior
      )

      this.periodoSelecionado = periodoExistente || this.periodos[0] || null
    },

    /**
     * Atualiza os componentes conforme o período selecionado e emite o periodo selecionado.
     *
     * @param {Object|null} periodo Período selecionado.
     * @return {void}
     */
    alterarPeriodo(periodo) {
      this.fecharModalEquipes()

      if (!periodo?.value) {
        this.dadosComponentesPeriodo = []
        return
      }

      this.filtrarComponentesPorPeriodo()
      this.emitirPeriodoSelecionado()
    },

    /**
     * Filtra os componentes pelo período selecionado.
     *
     * @return {void}
     */
    filtrarComponentesPorPeriodo() {
      const mesAno = this.periodoSelecionado?.value

      if (!mesAno) {
        this.dadosComponentesPeriodo = []
        return
      }

      this.dadosComponentesPeriodo = this.dadosComponentesRaw.filter(
        (item) => String(item.mes_ano || '').trim() === mesAno
      )
    },

    /**
     * Altera a visualização entre os resultados previstos e realizados.
     *
     * Ao selecionar realizado, mantém no select somente os meses 4, 8 e 12
     * e seleciona automaticamente o período quadrimestral mais recente.
     *
     * Ao voltar para previsto, todos os períodos ficam disponíveis novamente.
     *
     * @param {String} tipo Tipo selecionado.
     * @return {void}
     */
    alterarTipoResultado(tipo) {
      const periodoAnterior = this.periodoSelecionado?.value

      this.tipoPrevistoRealizado =
        tipo === 'realizado' ? 'realizado' : 'previsto'

      this.fecharModalEquipes()

      if (this.tipoPrevistoRealizado === 'realizado') {
        this.periodoSelecionado = this.periodosFiltrados[0] || null
      } else {
        const periodoExistente = this.periodos.find(
          (periodo) => periodo.value === periodoAnterior
        )

        this.periodoSelecionado = periodoExistente || this.periodos[0] || null
      }

      this.filtrarComponentesPorPeriodo()
      this.emitirPeriodoSelecionado()
    },

    /**
     * Fecha e limpa o modal de equipes.
     *
     * @return {void}
     */
    fecharModalEquipes() {
      this.modalEquipes.visible = false
      this.modalEquipes.loading = false
      this.modalEquipes.titulo = ''
      this.modalEquipes.items = []
      this.modalEquipes.indicador = null
    },

    /**
     * Monta os dados de um componente para exibição no gráfico.
     *
     * @param {Object} componente Configuração do componente.
     * @param {Object|undefined} row Registro retornado pela API.
     * @return {Object}
     */
    montarComponente(componente, row) {
      const sufixo =
        this.tipoPrevistoRealizado === 'realizado' ? 'realizada' : 'prevista'

      const campoDados =
        this.tipoPrevistoRealizado === 'realizado'
          ? 'dados_realizados'
          : 'dados_previstos'

      const campoPercentual =
        this.tipoPrevistoRealizado === 'realizado'
          ? 'percent_equipes_otimas_realizada'
          : 'percent_equipes_otimas_prevista'

      const equipes = this.normalizarDadosEquipes(row?.[campoDados])

      const otima = Number(row?.[`qtd_equipes_otimas_${sufixo}`] || 0)
      const boa = Number(row?.[`qtd_equipes_bom_${sufixo}`] || 0)
      const suficiente = Number(row?.[`qtd_equipes_suficiente_${sufixo}`] || 0)
      const regular = Number(row?.[`qtd_equipes_regular_${sufixo}`] || 0)

      return {
        id: componente.id,
        indicador: componente.id,
        nome: componente.nome,

        total: otima + boa + suficiente + regular,

        otima,
        boa,
        suficiente,
        regular,

        percentualOtimas: Number(row?.[campoPercentual] || 0),

        equipes: {
          otima: this.normalizarEquipesTabela(equipes.otimo, componente.id),
          boa: this.normalizarEquipesTabela(equipes.bom, componente.id),
          suficiente: this.normalizarEquipesTabela(
            equipes.suficiente,
            componente.id
          ),
          regular: this.normalizarEquipesTabela(equipes.regular, componente.id),
        },
      }
    },

    /**
     * Normaliza o JSON das equipes agrupadas por classificação.
     *
     * @param {Object|String|null} dados Dados retornados pela API.
     * @return {Object}
     */
    normalizarDadosEquipes(dados) {
      const vazio = {
        otimo: [],
        bom: [],
        suficiente: [],
        regular: [],
      }

      if (!dados) {
        return vazio
      }

      try {
        const dadosNormalizados =
          typeof dados === 'string' ? JSON.parse(dados) : dados

        return {
          otimo: Array.isArray(dadosNormalizados?.otimo)
            ? dadosNormalizados.otimo
            : [],

          bom: Array.isArray(dadosNormalizados?.bom)
            ? dadosNormalizados.bom
            : [],

          suficiente: Array.isArray(dadosNormalizados?.suficiente)
            ? dadosNormalizados.suficiente
            : [],

          regular: Array.isArray(dadosNormalizados?.regular)
            ? dadosNormalizados.regular
            : [],
        }
      } catch (error) {
        console.warn('Falha ao normalizar os dados dos componentes:', error)
        return vazio
      }
    },

    /**
     * Adapta os campos das equipes para o formato esperado pelas tabelas.
     *
     * Os dados específicos do CVAT são preservados para uso no CvatDataTable.
     * Os demais componentes mantêm o all_indicadores utilizado pelo
     * EquipesDataTable.
     *
     * @param {Array} equipes Lista de equipes.
     * @param {Number} indicador Identificador do componente.
     * @return {Array}
     */
    normalizarEquipesTabela(equipes, indicador) {
      if (!Array.isArray(equipes)) {
        return []
      }

      const idIndicador = Number(indicador)

      return equipes.map((equipe) => {
        const itemNormalizado = {
          ...equipe,

          indicador: Number(equipe.indicador || idIndicador),

          ine: equipe.ine || equipe.nu_ine || '',
          nu_ine: equipe.nu_ine || equipe.ine || '',

          no_ubs:
            equipe.no_ubs || equipe.no_unidade_saude || equipe.no_unidade || '',

          no_unidade_saude:
            equipe.no_unidade_saude || equipe.no_ubs || equipe.no_unidade || '',
        }

        if (idIndicador === 56) {
          return itemNormalizado
        }

        const allIndicadores = Array.isArray(equipe.all_indicadores)
          ? equipe.all_indicadores
          : this.normalizarArrayJson(equipe.all_indicadores)

        return {
          ...itemNormalizado,

          all_indicadores: allIndicadores.map((item) => ({
            ...item,
            indicador: Number(item.indicador || idIndicador),
          })),
        }
      })
    },

    /**
     * Converte um valor JSON em array.
     *
     * @param {Array|String|null} valor Valor que será normalizado.
     * @return {Array}
     */
    normalizarArrayJson(valor) {
      if (Array.isArray(valor)) {
        return valor
      }

      if (!valor) {
        return []
      }

      try {
        const dados = typeof valor === 'string' ? JSON.parse(valor) : valor
        return Array.isArray(dados) ? dados : []
      } catch (error) {
        console.warn('Falha ao normalizar array JSON:', error)
        return []
      }
    },

    /**
     * Abre o modal com as equipes da classificação selecionada.
     *
     * @param {Object} slice Fatia selecionada no gráfico.
     * @return {void}
     */
    abrirModalEquipesClassificacao(slice) {
      if (!slice?.key || !slice?.item?.id) {
        return
      }

      this.modalEquipes.indicador = Number(slice.item.id)
      this.modalEquipes.visible = true
      this.modalEquipes.loading = false
      this.modalEquipes.titulo = `${slice.item.nome} - ${slice.label}`
      this.modalEquipes.items = slice.item?.equipes?.[slice.key] || []
    },

    /**
     * Retorna a cor da classificação predominante.
     *
     * @param {Object} item Dados do componente.
     * @return {String}
     */
    corPredominante(item) {
      const classificacoes = [
        {
          key: 'otima',
          value: Number(item?.otima || 0),
          color: '#1212b6',
          prioridade: 4,
        },
        {
          key: 'boa',
          value: Number(item?.boa || 0),
          color: '#38ba38',
          prioridade: 3,
        },
        {
          key: 'suficiente',
          value: Number(item?.suficiente || 0),
          color: '#d5ba0a',
          prioridade: 2,
        },
        {
          key: 'regular',
          value: Number(item?.regular || 0),
          color: '#c46500',
          prioridade: 1,
        },
      ]

      const predominante = classificacoes.sort((a, b) => {
        if (b.value !== a.value) {
          return b.value - a.value
        }

        return b.prioridade - a.prioridade
      })[0]

      return predominante?.value > 0 ? predominante.color : '#e5e7eb'
    },

    /**
     * Limita o percentual ao intervalo entre zero e cem.
     *
     * @param {Number|String} percentual Percentual informado.
     * @return {Number}
     */
    limitarPercentual(percentual) {
      const valor = Number(percentual || 0)

      if (!Number.isFinite(valor)) {
        return 0
      }

      return Math.min(100, Math.max(0, valor))
    },

    /**
     * Formata o percentual para exibição.
     *
     * @param {Number|String} percentual Percentual informado.
     * @return {Number|String}
     */
    formatarPercentual(percentual) {
      const valor = Number(percentual || 0)

      if (!Number.isFinite(valor)) {
        return 0
      }

      return Number.isInteger(valor) ? valor : valor.toFixed(2)
    },
  },
}
</script>

<style scoped lang="scss">
.componentes-tabs {
  width: 100%;
}

.componentes-filtros {
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 18px;
  margin-bottom: 24px;
}

.periodo-field {
  width: 230px;

  label {
    display: block;
    margin-bottom: 6px;
    color: #374151;
    font-size: 13px;
    font-weight: 700;
  }
}

.previsto-realizado-field {
  display: flex;
  align-items: flex-end;
}

.loading-componentes {
  min-height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #6b7280;
  font-size: 14px;
}

.componentes-legend-wrap {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 18px;
}

.componentes-legend {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 16px;
  padding: 10px 14px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  text-transform: uppercase;
  font-size: 13px;
}

.legend-dot {
  width: 11px;
  height: 11px;
  flex: 0 0 11px;
  border-radius: 50%;
}

.legend-label {
  color: #4b5563;
  font-size: 12px;
  font-weight: 700;
}

.componentes-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(210px, 1fr));
  gap: 18px;
}

.componente-card {
  min-width: 0;
  display: flex;
  flex-direction: column;
  padding: 18px;
  background: #ffffff;
  border: 2px solid #e5e7eb;
  border-radius: 15px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.07);
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 9px 24px rgba(15, 23, 42, 0.11);
  }
}

.componente-title {
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1f2937;
  font-size: 14px;
  font-weight: 800;
  line-height: 1.35;
  text-align: center;
}

.chart-wrap {
  min-height: 245px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 5px 0 15px;
}

.otimas-wrap {
  margin-top: auto;
  padding-top: 15px;
  border-top: 1px solid #edf0f4;
}

.otimas-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
  color: #64748b;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.35px;
  text-transform: uppercase;

  strong {
    color: #1f2937;
    font-size: 13px;
  }
}

.otimas-bar {
  width: 100%;
  height: 8px;
  overflow: hidden;
  background: #edf0f4;
  border-radius: 999px;
}

.otimas-fill {
  height: 100%;
  background: linear-gradient(
    90deg,
    #c46500 0%,
    #d5ba0a 34%,
    #38ba38 67%,
    #1212b6 100%
  );
  border-radius: inherit;
  transition: width 0.5s ease;
}

.empty-state {
  padding: 35px 20px;
  color: #6b7280;
  text-align: center;
}

.tag-periodo,
.tag-resultado {
  background: #d6d2d2;
  color: #000;
  padding: 5px 10px;
  border-radius: 32px;
  font-size: 13px;
  margin-left: 5px;
  font-weight: 700;
}

@media screen and (max-width: 1200px) {
  .componentes-grid {
    grid-template-columns: repeat(2, minmax(230px, 1fr));
  }
}

@media screen and (max-width: 700px) {
  .componentes-filtros {
    align-items: stretch;
    flex-direction: column;
  }

  .periodo-field,
  .previsto-realizado-field {
    width: 100%;
  }

  .componentes-legend-wrap {
    justify-content: center;
  }

  .componentes-legend {
    justify-content: center;
  }

  .componentes-grid {
    grid-template-columns: 1fr;
  }
}
</style>
